import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Badge from "../components/Badge";
import StatusChip from "../components/StatusChip";
import IconButton from "../components/IconButton";
import { AudioIcon, VideoIcon, PlayIcon } from "../components/icons";
import MediaIndicator from "../components/MediaIndicator";
import { CategoryIconDisc, categoryKeyForClass } from "../components/categoryIcons";
import { usePlayer } from "../player/PlayerProvider";
import { fmt } from "../player/MiniPlayer";
import { paths } from "../routing/paths";
import { getLatestClasses, getPopularClasses, getQaHighlights, getClassSections } from "../api/redesignApi";

/*
 * Zone 4 — Explore (Homepage reference): segmented By topic / By subject
 * toggle over chip rails, Popular series cards (Hebrew lockups), Latest
 * classes list rows (real data, playable in place), Q&A highlight cards +
 * "Ask your own question".
 *
 * Topic chips route through search (the RAG concept layer is the topic
 * index); subject chips are the real top-level archive categories.
 * Popularity ranking for series needs backend (BACKEND-TODO.md) — the set
 * below is the curated flagship list from the design reference.
 */

const TOPIC_CHIPS = [
  "Emunah",
  "Davening",
  "Simcha",
  "Relationships",
  "Bitachon (בטחון)",
  "Mesiras Nefesh",
];

const SUBJECT_CHIPS = [
  { label: "Tanya", note: "8 series", slug: "tanya" },
  { label: "Ma'amarim", note: "by Rebbe", slug: "maamarim-2" },
  { label: "Sichos", slug: "sichos" },
  { label: "Parsha", note: "by Sefer", slug: "weekly-parsha" },
  { label: "Tefilla", slug: "tefilla-prayer" },
  { label: "Jewish & Chassidic Calendar", slug: "jewish-and-chassidic-calendar" },
  { label: "Hayom Yom", slug: "hayom-yom-2" },
  { label: "Talks", slug: "life-lessons" },
  { label: "Kabbalah & history", slug: "thought-and-history" },
  { label: "Likutei Torah", slug: "likutei-torah" },
];

const POPULAR_SERIES = [
  {
    hebrew: "תניא",
    kicker: "TANYA LECTURES",
    corner: "4 sections",
    title: "Tanya Lectures",
    meta: "Chapter by chapter, from inside the text",
    slug: "291-tanya-lectures",
  },
  {
    hebrew: "מאמרים",
    kicker: "MA'AMARIM",
    corner: "By Rebbe · 7 sub-series",
    title: "Ma'amarim — by Rebbe",
    meta: "Text-based · with source PDFs",
    slug: "maamarim-2",
  },
  {
    hebrew: "שיחות",
    kicker: "SICHOS",
    corner: "Likutei Sichos & more",
    title: "Sichos",
    meta: "Talks of the Rebbeim, from the sources",
    slug: "sichos",
  },
  {
    hebrew: "א־ב",
    kicker: "CHASSIDIC GLOSSARY",
    corner: "2 orderings",
    titleNode: (
      <>
        Chassidic Glossary <span dir="rtl">באותיות אל"ף בית</span>
      </>
    ),
    title: "Chassidic Glossary",
    meta: 'Every term → its "start here" classes',
    slug: "chassidic-glossary",
  },
];

const chipClasses =
  "flex-shrink-0 inline-flex items-center gap-2 text-[16px] font-semibold leading-6 text-ic-body bg-white border border-ic-borderGray px-5 py-2.5 rounded-3xl no-underline transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue hover:no-underline focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft";

function SectionHead({ title, linkLabel, linkTo }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
        {title}
      </h2>
      {linkLabel && (
        <Link
          to={linkTo}
          className="text-[14px] leading-5 text-ic-blue font-semibold flex-shrink-0 py-3 px-2 -my-3 -mx-2 inline-flex items-center min-h-[44px] box-border hover:underline"
        >
          {linkLabel}
        </Link>
      )}
    </div>
  );
}

function LatestRow({ item, last, sections }) {
  const player = usePlayer();
  // The class's category icon (nearest ancestor with one); the media tile
  // only stands in when the category can't be told.
  const iconKey = categoryKeyForClass({
    slugs: [...(sections || []), item.category?.type === "category" ? item.category.slug : null],
    title: item.title,
  });
  return (
    <div className={`flex items-center gap-3 px-4 py-3.5 ${last ? "" : "border-b border-ic-lightGray"} transition-colors duration-150 ease-out hover:bg-ic-offwhite`}>
      {iconKey ? (
        <span className="flex-shrink-0 w-[52px] flex justify-center" aria-hidden="true">
          <CategoryIconDisc categoryKey={iconKey} size={48} iconSize={34} />
        </span>
      ) : (
        <MediaIndicator media={item.media || item.mediaKind} />
      )}
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        {(item.category || item.isNew) && (
          <div className="flex items-center gap-2 flex-wrap">
            {item.category && (
              <Link
                to={item.category.type === "series" ? paths.series(item.category.slug) : paths.permalink(item.category.slug)}
                className="bg-ic-lightGray text-ic-body text-[11px] font-semibold tracking-[0.5px] leading-4 px-2.5 py-0.5 rounded-xl whitespace-nowrap no-underline transition-colors duration-150 ease-out hover:bg-ic-borderGray hover:text-ic-navy hover:no-underline"
              >
                {item.category.title}
              </Link>
            )}
            {item.isNew && (
              <StatusChip kind="new" size="sm">NEW</StatusChip>
            )}
          </div>
        )}
        <Link
          to={paths.permalink(item.slug)}
          className="text-[18px] font-semibold leading-7 text-black no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere inline-flex items-center py-2 -my-2 min-h-[44px]"
        >
          {item.title}
        </Link>
        <span className="text-[14px] leading-5 text-ic-slate">
          {[
            item.duration || (item.durationSeconds ? fmt(item.durationSeconds) : null),
            item.media === "both" ? "Audio + Video" : item.media === "video" || item.mediaKind === "video" ? "Video" : "Audio",
            item.dateLabel,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
      <IconButton
        variant="circleOutline"
        size={48}
        label={`Play: ${item.title}`}
        onClick={() =>
          player.load({ title: item.title, mediaKind: item.mediaKind, src: item.src, href: paths.permalink(item.slug) }, { autoplay: true })
        }
      >
        <PlayIcon size={20} />
      </IconButton>
    </div>
  );
}

export default function ExploreShelves() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("topics");
  const [latest, setLatest] = useState([]);
  const [popular, setPopular] = useState([]);
  const [qa, setQa] = useState([]);
  const [sections, setSections] = useState({});

  useEffect(() => {
    getLatestClasses(3).then(setLatest);
    getPopularClasses(3).then(setPopular);
    getQaHighlights(4).then(setQa);
  }, []);

  useEffect(() => {
    const ids = [...latest, ...popular].map((i) => i.id).filter((id) => !(id in sections));
    if (ids.length === 0) return;
    getClassSections(ids).then((data) => setSections((prev) => ({ ...prev, ...data })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest, popular]);

  const tabButton = (key, label) => {
    const active = tab === key;
    return (
      <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => setTab(key)}
        className={
          active
            ? "h-10 px-5 font-sans text-[16px] font-bold leading-6 text-ic-offwhite bg-ic-navy border-0 rounded-3xl cursor-pointer shadow-ic-raised focus-visible:outline-none focus-visible:shadow-ic-focus"
            : "h-10 px-5 font-sans text-[16px] font-semibold leading-6 text-ic-body bg-transparent border-0 rounded-3xl cursor-pointer transition-colors duration-150 ease-out hover:text-ic-blue focus-visible:outline-none focus-visible:shadow-ic-focus"
        }
      >
        {label}
      </button>
    );
  };

  return (
    <section className="max-w-[1280px] mx-auto px-5 pt-14 w-full flex flex-col gap-10">
      {/* Topics / subjects */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
            Explore
          </h2>
          <div role="tablist" aria-label="Explore by topic or subject" className="inline-flex bg-ic-lightGray rounded-3xl p-1 gap-1">
            {tabButton("topics", "By topic")}
            {tabButton("subjects", "By subject")}
          </div>
        </div>
        {tab === "topics" ? (
          <div className="ic-rail flex gap-2 overflow-x-auto pb-1">
            {TOPIC_CHIPS.map((topic) => (
              <Link key={topic} to={paths.search(topic.replace(/\s*\(.*\)$/, ""))} className={chipClasses}>
                {topic}
              </Link>
            ))}
          </div>
        ) : (
          <div className="ic-rail flex gap-2 overflow-x-auto pb-1">
            {SUBJECT_CHIPS.map((s) => (
              <Link key={s.slug} to={paths.permalink(s.slug)} className={chipClasses}>
                {s.label}
                {s.note && <span className="text-[12px] font-bold text-ic-slate">{s.note}</span>}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Popular classes — most viewed in the last 24h; hidden until data lands */}
      {popular.length > 0 && (
        <div className="flex flex-col gap-3">
          <SectionHead title="Popular classes" />
          <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
            {popular.map((item, i) => (
              <LatestRow key={item.id} item={item} sections={sections[item.id]} last={i === popular.length - 1} />
            ))}
          </div>
        </div>
      )}

      {/* Popular series */}
      <div className="flex flex-col gap-3">
        <SectionHead title="Popular series" />
        <div className="ic-rail flex gap-4 overflow-x-auto pb-2 items-stretch">
          {POPULAR_SERIES.map((s, i) => (
            <Link
              key={s.slug}
              to={paths.permalink(s.slug)}
              aria-label={`Series: ${s.title}`}
              className="bg-white border border-ic-lightGray rounded-xl overflow-hidden shadow-ic-raised no-underline hover:no-underline flex flex-col transition-all duration-150 ease-out hover:shadow-ic-elevated hover:border-ic-borderGray focus-visible:outline-none focus-visible:shadow-ic-focus-soft"
              style={{ flex: "0 0 clamp(200px, 56vw, 280px)" }}
            >
              <div
                className={`relative ${i % 2 === 0 ? "bg-ic-navy" : "bg-ic-navyCard"} flex flex-col items-center justify-center gap-0.5`}
                style={{ height: "clamp(112px, 31vw, 158px)" }}
              >
                <span dir="rtl" className="font-extrabold text-ic-sky" style={{ fontSize: "clamp(30px, 8vw, 40px)", lineHeight: 1.2 }}>
                  {s.hebrew}
                </span>
                <span className="text-[11px] font-bold tracking-[0.5px] text-ic-lightGray">{s.kicker}</span>
                <span className="absolute right-2 bottom-2 bg-[rgba(22,30,46,0.9)] border border-white/20 text-white text-[11px] font-bold px-2 py-0.5 rounded-md">
                  {s.corner}
                </span>
              </div>
              <div className="px-4 py-3 flex flex-col gap-1.5">
                <span className="self-start"><Badge variant="primary">SERIES</Badge></span>
                <span className="text-[16px] font-semibold leading-6 text-black">{s.titleNode || s.title}</span>
                <span className="text-[14px] leading-5 text-ic-slate">{s.meta}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Latest classes */}
      <div className="flex flex-col gap-3">
        <SectionHead title="Latest classes" linkLabel="All new" linkTo={paths.latestClasses()} />
        <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
          {latest.length === 0 ? (
            <div className="px-4 py-8 text-center text-[14px] text-ic-slate">
              New classes are added all the time — check back shortly.
            </div>
          ) : (
            latest.map((item, i) => <LatestRow key={item.id} item={item} sections={sections[item.id]} last={i === latest.length - 1} />)
          )}
        </div>
      </div>

      {/* Q&A highlights */}
      <div className="flex flex-col gap-3">
        <SectionHead title="Real questions, real answers" linkLabel="All Q&A" linkTo={paths.permalink("qa")} />
        <div className="ic-rail flex gap-4 overflow-x-auto pb-2 items-stretch">
          {(qa.length ? qa : []).map((q) => (
            <Link
              key={q.id}
              to={paths.permalink(q.slug)}
              className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-5 no-underline hover:no-underline flex flex-col gap-2.5 transition-all duration-150 ease-out hover:shadow-ic-elevated hover:border-ic-borderGray focus-visible:outline-none focus-visible:shadow-ic-focus-soft"
              style={{ flex: "0 0 clamp(220px, 62vw, 300px)" }}
            >
              <span className="self-start"><Badge variant="secondary">Q&A</Badge></span>
              <span className="text-[18px] font-semibold leading-7 text-black ic-wrap-anywhere">{q.title}</span>
              <span className="mt-auto text-[14px] leading-5 text-ic-slate">Answered by Rabbi Paltiel</span>
            </Link>
          ))}
          {qa.length === 0 && (
            <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-5 flex flex-col gap-2.5" style={{ flex: "0 0 clamp(220px, 62vw, 300px)" }}>
              <span className="self-start"><Badge variant="secondary">Q&A</Badge></span>
              <span className="text-[18px] font-semibold leading-7 text-black">
                Hundreds of real questions, answered from the sources
              </span>
              <span className="mt-auto text-[14px] leading-5 text-ic-slate">Browse the Q&A archive</span>
            </div>
          )}
        </div>
        <div className="flex justify-start">
          <button
            type="button"
            onClick={() => navigate(paths.askQuestion())}
            className="bg-transparent text-ic-body font-sans text-[16px] font-semibold leading-6 px-5 py-2.5 border border-ic-borderGray rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue active:bg-black/5 focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
          >
            Ask your own question
          </button>
        </div>
      </div>
    </section>
  );
}
